import { getApolloClient } from '@/lib/apollo-client'
import { getBestFitUnitOfLength, type UnitType } from '@/lib/math'
import { getItem } from '@/plugins/storage'
import { getElevationAtCoord } from '@/services/elevation-service'
import { camera, isPointInView, queryCoord } from '@/services/map-service'
import {
  getLocationContext as getToolboxLocationContext,
  type LocationAnchor,
  type LocationContext,
  type LocationContextOptions,
  type ToolboxMapViewer,
} from '@onxmaps/ai-toolkit/toolbox'

/**
 * The queried point when one is set and still on screen, otherwise the visible
 * map center. `queryCoord` is the point the user last asked about — a map
 * click, waypoint, or deep link — so it tracks intent rather than mouse travel.
 */
const getDefaultAnchor = (): LocationAnchor | null => {
  const query = queryCoord()
  if (query && isPointInView(query)) {
    return { coord: query, source: 'queried point' }
  }
  const cam = camera()
  const center = cam?.location
  return center ? { coord: center, source: 'map center' } : null
}

const EMPTY_LOCATION_CONTEXT: LocationContext = { text: '', stateAbbr: null }

/**
 * Single-shot location context for the AI: anchor coords, elevation, state,
 * nearest populated place, and cell-coverage carriers. Defaults to the queried
 * point (falling back to the visible map center).
 */
export async function getLocationContext(
  viewer: Pick<ToolboxMapViewer, 'getZoom'>,
  options?: LocationContextOptions
): Promise<LocationContext> {
  const anchor = getDefaultAnchor()
  if (!anchor) {
    return EMPTY_LOCATION_CONTEXT
  }

  return await getToolboxLocationContext(
    {
      viewer,
      client: getApolloClient(),
      getAnchor: () => anchor,
      getElevation: () =>
        getElevationAtCoord([[anchor.coord.longitude, anchor.coord.latitude]]),
      formatDistance: (meters) =>
        getBestFitUnitOfLength({
          length: meters,
          from: 'meters',
          unitType: getItem<UnitType>('unitType') ?? 'imperial',
        }),
    },
    options
  )
}
